import { Tag } from 'src/shared/types/model.types';

// ============================================================================
// Tag Mutation Helper Functions
// These pure functions handle optimistic updates for tag mutations
// ============================================================================

/**
 * Removes a tag from the model's tags array
 */
export function removeTagFromTags(tags: Tag[], tagId: string): Tag[] {
  return tags.filter((tag) => tag.id !== tagId);
}

/**
 * Removes a tag from its parent's children array
 */
export function removeTagFromParentChildren(tags: Tag[], tagId: string, parentId: string): Tag[] {
  const parentIndex = tags.findIndex((tag) => tag.id === parentId);
  if (parentIndex === -1) return tags;

  const updatedTags = [...tags];
  const parent = updatedTags[parentIndex];
  updatedTags[parentIndex] = {
    ...parent,
    children: parent.children.filter((child) => child.id !== tagId),
  };

  return updatedTags;
}

/**
 * Adds a tag to its parent's children array
 */
export function addTagToParentChildren(tags: Tag[], tagId: string, parentId: string): Tag[] {
  const parentIndex = tags.findIndex((tag) => tag.id === parentId);
  if (parentIndex === -1) return tags;

  const updatedTags = [...tags];
  const parent = updatedTags[parentIndex];
  updatedTags[parentIndex] = {
    ...parent,
    children: [...parent.children, { id: tagId }],
  };

  return updatedTags;
}

/**
 * Ensures a tag is in its parent's children array (idempotent)
 */
export function ensureTagInParentChildren(tags: Tag[], tagId: string, parentId: string): Tag[] {
  const parentIndex = tags.findIndex((tag) => tag.id === parentId);
  if (parentIndex === -1) return tags;

  const parent = tags[parentIndex];
  const childAlreadyExists = parent.children.some((child) => child.id === tagId);

  if (childAlreadyExists) {
    return tags;
  }

  return addTagToParentChildren(tags, tagId, parentId);
}

/**
 * Handles parent-child relationship changes when a tag is moved
 * @param oldParentId - The old parent ID (undefined/null means no parent)
 * @param newParentId - The new parent ID (undefined/null means no parent)
 */
export function handleParentChildRelationshipChange(
  tags: Tag[],
  tagId: string,
  oldParentId: string | null | undefined,
  newParentId: string | null | undefined
): Tag[] {
  let updatedTags = tags;

  // Normalize undefined to null
  const normalizedOldParentId = oldParentId ?? null;
  const normalizedNewParentId = newParentId ?? null;

  // Remove from old parent's children array
  if (normalizedOldParentId) {
    updatedTags = removeTagFromParentChildren(updatedTags, tagId, normalizedOldParentId);
  }

  // Add to new parent's children array
  if (normalizedNewParentId) {
    updatedTags = addTagToParentChildren(updatedTags, tagId, normalizedNewParentId);
  }

  return updatedTags;
}

/**
 * Cascades tag acceptance up the parent chain
 * When a child tag is accepted, all its parents should also be accepted
 */
export function cascadeParentAcceptance(tags: Tag[], startingTagId: string): Tag[] {
  const startingTag = tags.find((tag) => tag.id === startingTagId);
  if (!startingTag) return tags;

  let updatedTags = [...tags];

  // First, ensure the starting tag is in its parent's children array
  if (startingTag.parentId) {
    updatedTags = ensureTagInParentChildren(updatedTags, startingTagId, startingTag.parentId);
  }

  // Walk up the parent chain and accept all parents that need accepting
  let currentParentId = startingTag.parentId;
  while (currentParentId) {
    const parentIndex = updatedTags.findIndex((tag) => tag.id === currentParentId);
    if (parentIndex === -1) break; // Parent not found, stop

    const parentTag = updatedTags[parentIndex];

    // Only update if parent is not already in a favorable state
    if (!['ACTIVE', 'PENDING_UPDATED', 'PENDING_ADDED'].includes(parentTag.status)) {
      updatedTags[parentIndex] = {
        ...parentTag,
        status: 'PENDING_ADDED',
        // If parent is an autoTag, also change its type to manual
        type: parentTag.type === 'auto' && parentTag.status === 'SUGGESTED' ? 'manual' : parentTag.type,
      };
    }

    // Move up to the next parent
    currentParentId = parentTag.parentId ?? null;
  }

  return updatedTags;
}

/**
 * Type definition for tag update patch
 * Matches the patch parameter from TagMutationProps
 */
export type TagUpdatePatch = {
  shortName?: string;
  description?: string;
  parentId?: string | null;
  extra?: Record<string, any>;
  nerExamples?: string[];
  feedback?: Tag['feedback'];
  isGrouped?: boolean;
  rules?: Tag['rules'];
  type?: 'auto' | 'manual' | 'imported' | 'sink';
  status?: 'ACTIVE' | 'REMOVED' | 'PENDING_ADDED' | 'PENDING_REMOVED' | 'PENDING_UPDATED' | 'SUGGESTED';
};

/**
 * Applies a tag update patch to the model's tags array
 * Handles all the complex logic for updating tags, parent-child relationships, and cascading updates
 */
export function applyTagUpdate(tags: Tag[], tagId: string, patch: TagUpdatePatch): Tag[] {
  const tagIndex = tags.findIndex((tag) => tag.id === tagId);
  if (tagIndex === -1) return tags;

  // If status is being set to REMOVED, remove the tag entirely
  if (patch.status === 'REMOVED') {
    return removeTagFromTags(tags, tagId);
  }

  // Otherwise, update the tag with the patch
  let updatedTags = [...tags];
  const currentTag = updatedTags[tagIndex];
  const oldParentId = currentTag.parentId;

  // Update the tag with the patch (deep merge the extra field)
  updatedTags[tagIndex] = {
    ...currentTag,
    ...patch,
    extra: patch.extra ? { ...currentTag.extra, ...patch.extra } : currentTag.extra,
  };

  // Handle parent-child relationship changes (only if parentId is explicitly in the patch)
  if ('parentId' in patch) {
    const newParentId = patch.parentId;
    if (oldParentId !== (newParentId ?? null)) {
      updatedTags = handleParentChildRelationshipChange(updatedTags, tagId, oldParentId, newParentId);
    }
  }

  // If a child tag is being accepted, cascade acceptance up to all parents
  if (patch.status === 'PENDING_ADDED') {
    updatedTags = cascadeParentAcceptance(updatedTags, tagId);
  }

  return updatedTags;
}

/**
 * Creates an optimistic tag for new tag creation
 */
export function createOptimisticTag(
  shortName: string,
  internalId: string,
  description: string,
  parentId: string,
  isGrouped: boolean,
  parentHierarchy: string[]
): Tag {
  return {
    id: `temp-${Date.now()}`,
    internalId,
    parentId: parentId.length > 0 ? parentId : null,
    children: [],
    type: 'manual',
    hierarchy: parentId ? [...parentHierarchy, shortName] : [shortName],
    shortName,
    description: description || '',
    extra: {},
    nerExamples: [],
    isGrouped: isGrouped || false,
    outputType: 'string[]',
    status: 'ACTIVE',
    feedback: [],
    TagExamples: [],
    rules: [],
  };
}

/**
 * Adds a new tag to the model's tags array and updates parent relationships
 */
export function addNewTagToModel(
  tags: Tag[],
  shortName: string,
  internalId: string,
  description: string,
  parentId: string,
  isGrouped: boolean
): Tag[] {
  const parentTag = parentId ? tags.find((t) => t.id === parentId) : undefined;
  const optimisticTag = createOptimisticTag(
    shortName,
    internalId,
    description,
    parentId,
    isGrouped,
    parentTag?.hierarchy || []
  );

  let updatedTags = [...tags, optimisticTag];

  // Add to parent's children array if needed
  if (parentId) {
    updatedTags = addTagToParentChildren(updatedTags, optimisticTag.id, parentId);
  }

  return updatedTags;
}
